import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Combobox, Dialog, Select, Stack, t, Text, Textarea, toast } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { pendingLabel } from './PendingButton';
import { openPullRequestOf, PullRequestConflict } from './PullRequestConflict';
import { useEnvironmentName } from '../lib/environments';
import { changeRef } from './catalogue/PullRequestNotice';

type Site = fleetState.components['schemas']['Running']['sites'][number];

/**
 * SPEC-032 « Rattacher »: a site the agent reads on the server and no
 * deployment declares. The person picks the project, then the application and
 * environment; the pull request of « Serveurs du projet » (SPEC-006) adds the
 * server to that deployment and declares the site's URL (RG-032-5).
 */
export function AttachSiteDialog({ server, site, project: initialProject, onClose, onOpened }: { server: string; site: Site; project?: string; onClose: () => void; onOpened: () => void }) {
  const envName = useEnvironmentName();
  const [query, setQuery] = useState('');
  // D27: from a project's Domaines tab, that project is chosen already.
  const [project, setProject] = useState<string | null>(initialProject ?? null);
  const [deployment, setDeployment] = useState<string | null>(null);
  const [url, setUrl] = useState(`https://${site.domains[0] ?? ''}`);
  const [justification, setJustification] = useState('');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const projects = useQuery({
    queryKey: ['attach-projects', query],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects', { params: { query: { q: query || undefined, view: 'all', pageSize: 20 } } })),
    placeholderData: keepPreviousData,
  });
  const links = useQuery({
    queryKey: ['server-links', project],
    queryFn: async () => unwrap(await api.maintenance.GET('/projects/{project}/server-links', { params: { path: { project: project! } } })),
    enabled: Boolean(project),
  });
  const deployments = (links.data?.rows ?? []).flatMap(row => Object.entries(row.cells).flatMap(([env, cell]) => (cell
    ? [{ value: cell.deployment, label: `${row.application} · ${envName(env)}`, servers: cell.servers.map(s => s.name), env }]
    : [])));
  const chosen = deployments.find(d => d.value === deployment);
  const blocking = (failure?.body.details?.blocking ?? []) as { code: string; message: string }[];
  const needsJustification = blocking.some(b => b.code === 'JUSTIFICATION_REQUIRED');

  const submit = async () => {
    if (!project || !chosen) return;
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/projects/{project}/server-links', { params: { path: { project } }, body: {
        links: { [chosen.value]: [...new Set([...chosen.servers, server])] },
        urls: { [chosen.value]: url },
        ...(justification.trim() ? { justifications: { [chosen.value]: justification.trim() } } : {}),
      } }));
      toast({ tone: 'pos', title: t('running.attachOpened', { ref: changeRef(pullRequest) }) });
      onOpened();
    } catch (e) {
      if (e instanceof ApiFailure) setFailure(e);
      else throw e;
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open
      size="md"
      onClose={onClose}
      title={t('running.attachTitle', { domain: site.domains[0] ?? '' })}
      description={t('running.attachLead', { server })}
      confirmLabel={pendingLabel(t('running.attachConfirm'), sending)}
      confirmDisabled={!chosen || sending || (needsJustification && justification.trim().length < 10)}
      onConfirm={() => void submit()}
    >
      <Stack gap={16}>
        <Combobox
          label={t('running.attachProject')}
          options={(projects.data?.items ?? []).map(p => ({ value: p.name, label: p.title, description: p.clientTitle }))}
          value={project}
          onChange={(v: string | null) => { setProject(v); setDeployment(null); setFailure(null); }}
          onQueryChange={setQuery}
          filter={false}
          loading={projects.isFetching}
          required
        />
        <Select
          label={t('running.attachDeployment')}
          options={deployments.map(d => ({ value: d.value, label: d.label }))}
          value={deployment}
          onChange={v => { setDeployment(v); setFailure(null); }}
          disabled={!project || links.isPending}
          required
        />
        <Select
          label={t('running.attachUrl')}
          options={site.domains.map(d => ({ value: `https://${d}`, label: `https://${d}` }))}
          value={url}
          onChange={v => v && setUrl(v)}
          hint={t('running.attachUrlHint')}
        />
        {links.data && !links.data.canEdit && <Alert tone="warn">{t('running.attachReadOnly')}</Alert>}
        {needsJustification && (
          <Textarea label={t('projectServers.justification')} rows={2} maxLength={300} value={justification} onChange={setJustification} />
        )}
        {failure && openPullRequestOf(failure) ? (
          <PullRequestConflict failure={failure} onClosed={() => setFailure(null)} />
        ) : failure && (
          <Alert tone="neg" title={failure.body.message}>
            {blocking.length ? <Stack gap={4}>{blocking.map(b => <Text key={b.message} variant="body-sm">{b.message}</Text>)}</Stack> : failure.body.hint}
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
}
